import { z } from 'zod';
import { INSIGHTS_FACEBOOK_SELECTABLE_DATE_PRESETS, INSIGHTS_TIME_SERIES_MULTI_OPTION_FIELD_OPERATIONS_V2, } from './constants.js';
import { isInsightsFiltersV2, migrateFormerColumnMetricsToActionMetric, stringifyInsightsFacebookActionMetric, } from './utils.js';
const dateFilterV2 = z.discriminatedUnion('operation', [
    z.object({
        field: z.string(),
        operation: z.enum(['=', '!=', '<', '>']),
        value: z.date(),
    }),
    z.object({
        field: z.string(),
        operation: z.enum(['between', 'notBetween']),
        start: z.date(),
        end: z.date(),
    }),
    z.object({
        field: z.string(),
        operation: z.enum(['withinLast']),
        interval: z.enum(['day', 'week', 'month', 'year']),
        value: z.number().int(),
    }),
    z.object({
        field: z.string(),
        operation: z.enum(['withinDatePreset']),
        value: z.nativeEnum(INSIGHTS_FACEBOOK_SELECTABLE_DATE_PRESETS),
    }),
]);
const numericFilterV2 = z
    .object({
    field: z.string(),
    operation: z.enum(['=', '!=', '>', '>=', '<', '<=']),
    value: z.number(),
})
    .or(z.object({
    field: z.string(),
    operation: z.enum(['between', 'notBetween']),
    min: z.number(),
    max: z.number(),
}));
const enumerableFilterV2 = z.object({
    field: z.string(),
    operation: z.enum(INSIGHTS_TIME_SERIES_MULTI_OPTION_FIELD_OPERATIONS_V2),
    values: z.array(z.string()),
});
const textFilterV2 = z.discriminatedUnion('operation', [
    z.object({
        field: z.string(),
        operation: z.enum(['isSet', 'isNotSet']),
    }),
    z.object({
        field: z.string(),
        operation: z.enum([
            '=',
            '!=',
            'contains',
            'notContains',
            'startsWith',
            'endsWith',
        ]),
        value: z.string(),
    }),
]);
const tagFilterV2 = z.object({
    field: z.literal('tag').or(z.literal('tagCategory')),
    operation: z.enum(['in', 'notIn']),
    values: z.array(z.string()),
});
const insightsValueFilterV2 = numericFilterV2
    .or(enumerableFilterV2)
    .or(textFilterV2)
    .or(dateFilterV2)
    .or(tagFilterV2);
const insightsFilterExpressionElement = insightsValueFilterV2.or(z.object({
    expressions: z.array(z.object({
        operand: z.lazy(() => insightsFilterExpressionElement),
        operator: z.enum(['and', 'or']),
    })),
}));
const insightsFilterExpressionNode = z.object({
    operand: insightsFilterExpressionElement,
    operator: z.enum(['and', 'or']),
});
export const insightsFilterExpressionSchema = z.array(insightsFilterExpressionNode);
const insightsFilterExpressionGroup = z.object({
    expressions: insightsFilterExpressionSchema,
});
export const insightsFilterV2 = insightsValueFilterV2;
const filtersV1ToV2OperationMap = {
    equals: '=',
    not: '!=',
    before: '<',
    after: '>',
    btw: 'between',
    nbtw: 'notBetween',
    isSet: 'isSet',
    isNotSet: 'isNotSet',
    is: '=',
    isnot: '!=',
    gt: '>',
    gte: '>=',
    lt: '<',
    lte: '<=',
    withinDaysAgo: 'withinLast',
    withinMonthsAgo: 'withinLast',
    withinWeeksAgo: 'withinLast',
    withinYearsAgo: 'withinLast',
    endsWith: 'endsWith',
    startsWith: 'startsWith',
    contains: 'contains',
    notcontains: 'notContains',
    in: 'in',
    notIn: 'notIn',
    withinDatePreset: 'withinDatePreset',
};
export function convertFiltersV1ToV2(filters) {
    if (isInsightsFiltersV2(filters))
        return filters;
    const newFilters = filters.map((f) => {
        const migratedToAction = migrateFormerColumnMetricsToActionMetric(f.metric);
        const x = (migratedToAction
            ? {
                ...f,
                metricType: 'action',
                metric: stringifyInsightsFacebookActionMetric(migratedToAction),
            }
            : f);
        if (x.metricType === 'action' ||
            x.metricType === 'custom' ||
            x.metricType === 'numerical' ||
            x.metricType === 'wizard') {
            const operation = filtersV1ToV2OperationMap[x.operation];
            if (operation === 'between' || operation === 'notBetween') {
                if (x.value2 == null)
                    return null; // Invalid V1 filter
                return {
                    field: x.metric,
                    operation,
                    min: x.value1,
                    max: x.value2,
                };
            }
            return {
                field: x.metric,
                operation,
                value: x.value1,
            };
        }
        else if (x.metricType === 'multi') {
            return {
                field: x.metric,
                operation: x.operation,
                values: x.value1,
            };
        }
        else if (x.metricType === 'date') {
            switch (x.operation) {
                case 'is':
                case 'isnot':
                case 'after':
                case 'before': {
                    return {
                        field: x.metric,
                        operation: filtersV1ToV2OperationMap[x.operation],
                        value: x.value,
                    };
                }
                case 'btw':
                case 'nbtw':
                    return {
                        field: x.metric,
                        operation: filtersV1ToV2OperationMap[x.operation],
                        start: x.start,
                        end: x.end,
                    };
                case 'withinDatePreset':
                    return {
                        field: x.metric,
                        operation: filtersV1ToV2OperationMap[x.operation],
                        value: x.value,
                    };
                default:
                    return {
                        field: x.metric,
                        operation: filtersV1ToV2OperationMap[x.operation],
                        interval: x.operation === 'withinDaysAgo'
                            ? 'day'
                            : x.operation === 'withinMonthsAgo'
                                ? 'month'
                                : x.operation === 'withinWeeksAgo'
                                    ? 'week'
                                    : 'year',
                        value: x.value,
                    };
            }
        }
        else if (x.metricType === 'text') {
            if (x.operation === 'isSet' || x.operation === 'isNotSet') {
                return {
                    field: x.metric,
                    operation: filtersV1ToV2OperationMap[x.operation],
                };
            }
            else {
                if (x.value1 == null)
                    return null; // Invalid V1 filter
                return {
                    field: x.metric,
                    operation: filtersV1ToV2OperationMap[x.operation],
                    value: x.value1,
                };
            }
        }
        return null;
    });
    return newFilters.filter((x) => !!x);
}
export function isInsightsFilterListAnExpression(filters) {
    if (filters.length === 0)
        return true;
    if (filters.length) {
        const first = filters[0];
        if ('operand' in first) {
            return true;
        }
    }
    return false;
}
export function convertSimpleInsightsFiltersToExpression(filters) {
    if (isInsightsFilterListAnExpression(filters)) {
        return filters;
    }
    const convertedFilters = isInsightsFiltersV2(filters)
        ? filters
        : convertFiltersV1ToV2(filters);
    return [
        {
            operand: {
                expressions: convertedFilters.map((filter) => ({
                    operand: filter,
                    operator: 'and',
                })),
            },
            operator: 'and',
        },
    ];
}
const INSIGHTS_EXPRESSION_OPERATOR_PRECEDENCE = { or: 1, and: 2 };
export function parseInsightsExpressionIntoExpressionTree(expression) {
    const operandStack = [];
    const operatorStack = [];
    for (let i = 0; i < expression.length; i++) {
        const node = expression[i];
        const operand = 'expressions' in node.operand
            ? parseInsightsExpressionIntoExpressionTree(node.operand.expressions)
            : node.operand;
        operandStack.push(operand);
        if (i < expression.length - 1) {
            while (operatorStack.length > 0 &&
                INSIGHTS_EXPRESSION_OPERATOR_PRECEDENCE[operatorStack[operatorStack.length - 1]] >= INSIGHTS_EXPRESSION_OPERATOR_PRECEDENCE[node.operator]) {
                const right = operandStack.pop();
                const left = operandStack.pop();
                const operator = operatorStack.pop();
                operandStack.push({ left, operator, right });
            }
            operatorStack.push(node.operator);
        }
    }
    while (operatorStack.length > 0) {
        const right = operandStack.pop();
        const left = operandStack.pop();
        const operator = operatorStack.pop();
        operandStack.push({ left, operator, right });
    }
    return operandStack[0];
}
//# sourceMappingURL=contracts_v2.js.map